// frontend/src/components/layout/NextStepCard/NextStepCard.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { tableService } from '../../../services/table.service';
import { PipelineService } from '../../../services/pipeline.service';
import { AnalysisService } from '../../../services/analysis.service';
import { EdaService } from '../../../services/eda.service';
import { EdaPrepareService } from '../../../services/edaPrepare.service';
import { MlExperimentService } from '../../../services/mlExperiment.service';
import { MlModelsService } from '../../../services/mlModels.service';
import './NextStepCard.css';

const RESET_KEY = 'nextstep_reset_em';

function getResetTs(): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(RESET_KEY);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function setResetTs(ts: number) {
  if (typeof window === 'undefined') return;
  if (ts > 0) {
    window.localStorage.setItem(RESET_KEY, String(ts));
  } else {
    window.localStorage.removeItem(RESET_KEY);
  }
}

function aposReset(dateStr: string | null | undefined, resetTs: number): boolean {
  if (!resetTs) return true;
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (!Number.isFinite(t)) return false;
  return t >= resetTs;
}

type StepKey =
  | 'loading'
  | 'error'
  | 'importar'
  | 'processar'
  | 'processar_falhou'
  | 'acompanhar'
  | 'analisar'
  | 'explorar'
  | 'preparar'
  | 'treinar'
  | 'predizer'
  | 'concluido';

interface StepDefinition {
  key: StepKey;
  icon: string;
  title: string;
  description: string;
  actionLabel: string;
  actionRoute: string;
  variant?: 'normal' | 'warning' | 'success';
}

interface DetectResult {
  step: StepKey;
  failedAt?: string;
  failedMessage?: string;
}

const STEPS: Record<string, StepDefinition> = {
  importar: {
    key: 'importar',
    icon: '',
    title: 'Comece importando seus dados',
    description: 'Suba um arquivo CSV para criar sua primeira tabela no sistema.',
    actionLabel: ' Importar Dados',
    actionRoute: '/csv-upload',
    variant: 'normal',
  },
  processar: {
    key: 'processar',
    icon: '',
    title: 'Rode o pipeline de processamento',
    description: 'Execute o ORC Pipeline para gerar o trace_id e processar os dados.',
    actionLabel: ' Processamento',
    actionRoute: '/orc',
    variant: 'normal',
  },
  processar_falhou: {
    key: 'processar_falhou',
    icon: '',
    title: 'Sua última execução falhou',
    description: 'O pipeline não concluiu com sucesso. Rode novamente.',
    actionLabel: ' Rodar novamente',
    actionRoute: '/orc',
    variant: 'warning',
  },
  acompanhar: {
    key: 'acompanhar',
    icon: '',
    title: 'Acompanhe a execução',
    description: 'Há uma execução em andamento. Veja o progresso em tempo real.',
    actionLabel: ' Acompanhamento',
    actionRoute: '/monitor',
    variant: 'normal',
  },
  analisar: {
    key: 'analisar',
    icon: '',
    title: 'Analise os dados processados',
    description: 'Veja estatísticas, alertas, distribuição do alvo e detalhes das colunas.',
    actionLabel: ' Análise',
    actionRoute: '/analysis',
    variant: 'normal',
  },
  explorar: {
    key: 'explorar',
    icon: '',
    title: 'Explore o dataset (EDA)',
    description: 'Avalie separabilidade, warnings e importância das features.',
    actionLabel: ' Exploração',
    actionRoute: '/eda',
    variant: 'normal',
  },
  preparar: {
    key: 'preparar',
    icon: '',
    title: 'Prepare os dados para ML',
    description: 'Veja as transformações aplicadas e o resumo da preparação.',
    actionLabel: ' Preparação',
    actionRoute: '/eda-prepare',
    variant: 'normal',
  },
  treinar: {
    key: 'treinar',
    icon: '',
    title: 'Treine e avalie modelos',
    description: 'Veja os experimentos, decisões e o melhor modelo.',
    actionLabel: ' Modelos',
    actionRoute: '/ml',
    variant: 'normal',
  },
  predizer: {
    key: 'predizer',
    icon: '',
    title: 'Faça uma predição',
    description: 'Use um modelo em produção para gerar resultados em tempo real.',
    actionLabel: ' Predições',
    actionRoute: '/ml-predict',
    variant: 'normal',
  },
};

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function tentar<T>(
  fn: () => Promise<T>,
  label: string
): Promise<{ ok: true; data: T } | { ok: false; label: string; message: string }> {
  try {
    const data = await fn();
    return { ok: true, data };
  } catch (err: any) {
    const message =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      'Falha ao conectar';
    console.warn(`[NextStepCard] falha em "${label}":`, message);
    return { ok: false, label, message };
  }
}

function comoArray<T = any>(v: any): T[] {
  return Array.isArray(v) ? v : [];
}

export const NextStepCard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<StepKey>('loading');
  const [failedAt, setFailedAt] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [temReset, setTemReset] = useState<boolean>(false);

  const detectar = useCallback(async (): Promise<DetectResult> => {
    if (!user?.id) {
      return { step: 'importar' };
    }

    const resetTs = getResetTs();

    const tabelas = await tentar(
      () => tableService.getTablesAndColumns(),
      'Importar Dados'
    );
    if (!tabelas.ok) {
      return { step: 'error', failedAt: tabelas.label, failedMessage: tabelas.message };
    }
    const listaTabelas = comoArray(tabelas.data).filter((t: any) =>
      aposReset(t?.created_at ?? t?.imported_at ?? t?.created_at_db, resetTs)
    );
    if (listaTabelas.length === 0) {
      return { step: 'importar' };
    }

    const historico = await tentar(
      () => PipelineService.obterHistorico(user.id),
      'Processamento'
    );
    if (!historico.ok) {
      return { step: 'error', failedAt: historico.label, failedMessage: historico.message };
    }
    const listaHist = comoArray(historico.data).filter((h: any) =>
      aposReset(h?.created_at ?? h?.started_at, resetTs)
    );
    if (listaHist.length === 0) {
      return { step: 'processar' };
    }

    const ultima = listaHist[0];
    const status = ultima?.status;

    if (status === 'STARTED' || status === 'RUNNING') {
      return { step: 'acompanhar' };
    }
    if (status === 'FAILED') {
      return { step: 'processar_falhou' };
    }
    if (status !== 'SUCCESS') {
      return { step: 'processar' };
    }

    const datasets = await tentar(
      () => AnalysisService.listDatasets(),
      'Análise'
    );
    if (!datasets.ok) {
      return { step: 'error', failedAt: datasets.label, failedMessage: datasets.message };
    }
    const listaAnalise = comoArray(datasets.data).filter((d: any) =>
      aposReset(d?.created_at, resetTs)
    );
    if (listaAnalise.length === 0) {
      return { step: 'analisar' };
    }

    const eda = await tentar(() => EdaService.listSnapshots(), 'Exploração');
    if (!eda.ok) {
      return { step: 'error', failedAt: eda.label, failedMessage: eda.message };
    }
    const listaEda = comoArray(eda.data).filter((s: any) =>
      aposReset(s?.created_at, resetTs)
    );
    if (listaEda.length === 0) {
      return { step: 'explorar' };
    }

    const edaPrep = await tentar(
      () => EdaPrepareService.listSnapshots(),
      'Preparação'
    );
    if (!edaPrep.ok) {
      return { step: 'error', failedAt: edaPrep.label, failedMessage: edaPrep.message };
    }
    const listaPrep = comoArray(edaPrep.data).filter((s: any) =>
      aposReset(s?.created_at, resetTs)
    );
    if (listaPrep.length === 0) {
      return { step: 'preparar' };
    }

    const exp = await tentar(
      () => MlExperimentService.listExperiments(),
      'Modelos'
    );
    if (!exp.ok) {
      return { step: 'error', failedAt: exp.label, failedMessage: exp.message };
    }
    const listaExp = comoArray(exp.data).filter((e: any) =>
      aposReset(e?.created_at, resetTs)
    );
    if (listaExp.length === 0) {
      return { step: 'treinar' };
    }

    const modelos = await tentar(
      () => MlModelsService.listModels(),
      'Predições'
    );
    if (!modelos.ok) {
      return { step: 'error', failedAt: modelos.label, failedMessage: modelos.message };
    }
    const listaModelos = comoArray(modelos.data).filter((m: any) =>
      aposReset(m?.created_at, resetTs)
    );
    if (listaModelos.length === 0) {
      return { step: 'predizer' };
    }

    return { step: 'concluido' };
  }, [user?.id]);

  const rodar = useCallback(async () => {
    setStep('loading');
    setFailedAt(null);
    setFailedMessage(null);
    setTemReset(getResetTs() > 0);

    const r = await detectar();
    setStep(r.step);
    setFailedAt(r.failedAt ?? null);
    setFailedMessage(r.failedMessage ?? null);
  }, [detectar]);

  const handleReverificar = useCallback(async () => {
    setRetrying(true);
    await delay(300);
    await rodar();
    setRetrying(false);
  }, [rodar]);

  const handleReiniciar = useCallback(() => {
    const ok = window.confirm(
      'Começar um novo fluxo?\n\n' +
        'O card vai ignorar tudo que você fez antes de agora. ' +
        'Seus dados anteriores continuam salvos no sistema.\n\n' +
        'Você pode desfazer clicando em " Ver tudo de novo".'
    );
    if (!ok) return;

    setResetTs(Date.now());
    setTemReset(true);
    setStep('importar');
    setFailedAt(null);
    setFailedMessage(null);
  }, []);

  const handleLimparReset = useCallback(() => {
    setResetTs(0);
    setTemReset(false);
    rodar();
  }, [rodar]);

  useEffect(() => {
    rodar();
  }, [rodar]);

  if (step === 'loading') {
    return (
      <div className="next-step-card loading">
        <div className="next-step-loading"> Verificando seu progresso...</div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="next-step-card error">
        <div className="next-step-header">
          <span className="next-step-icon"></span>
          <div>
            <h3>Não foi possível verificar seu progresso</h3>
            <p>
              {failedAt
                ? `A verificação parou em: ${failedAt}.`
                : 'Algo deu errado ao consultar o servidor.'}
            </p>
            {failedMessage && (
              <p className="next-step-error-detail">Detalhe: {failedMessage}</p>
            )}
            <p className="next-step-error-hint">
              Se o backend estiver fora do ar, aguarde alguns segundos e tente
              novamente.
            </p>
          </div>
        </div>
        <div className="next-step-actions">
          <button
            className="next-step-btn primary"
            onClick={handleReverificar}
            disabled={retrying}
          >
            {retrying ? ' Tentando...' : ' Tentar novamente'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'concluido') {
    return (
      <div className="next-step-card done">
        {temReset && (
          <div className="next-step-reset-badge">
             Fluxo reiniciado  mostrando apenas dados novos
          </div>
        )}
        <div className="next-step-header">
          <span className="next-step-icon"></span>
          <div>
            <h3>Você completou o fluxo!</h3>
            <p>
              Já importou, processou, analisou, treinou e promoveu um modelo.
            </p>
          </div>
        </div>
        <div className="next-step-actions">
          <button
            className="next-step-btn secondary"
            onClick={() => navigate('/ml-compare')}
          >
             Comparar Modelos
          </button>
          <button
            className="next-step-btn secondary"
            onClick={() => navigate('/ml-predict')}
          >
             Predições
          </button>
          <button
            className="next-step-btn secondary"
            onClick={() => navigate('/scientific-dashboard')}
          >
             Jornada dos Dados
          </button>
          <button
            className="next-step-btn outline"
            onClick={() => navigate('/csv-upload')}
          >
             Importar outro CSV
          </button>
        </div>
        <div className="next-step-footer">
          <button
            className="next-step-link"
            onClick={handleReverificar}
            disabled={retrying}
          >
            {retrying ? ' Reverificando...' : ' Reverificar'}
          </button>
          {temReset ? (
            <button
              className="next-step-link"
              onClick={handleLimparReset}
              disabled={retrying}
            >
               Ver tudo de novo
            </button>
          ) : (
            <button
              className="next-step-link danger"
              onClick={handleReiniciar}
              disabled={retrying}
            >
               Reiniciar fluxo
            </button>
          )}
        </div>
      </div>
    );
  }

  const def = STEPS[step] ?? STEPS['importar'];
  const variantClass =
    def.variant === 'warning'
      ? ' warning'
      : def.variant === 'success'
      ? ' done'
      : '';

  return (
    <div className={`next-step-card${variantClass}`}>
      {temReset && (
        <div className="next-step-reset-badge">
           Fluxo reiniciado  mostrando apenas dados novos
        </div>
      )}
      <div className="next-step-header">
        <span className="next-step-icon">{def.icon}</span>
        <div>
          <h3>{def.title}</h3>
          <p>{def.description}</p>
        </div>
      </div>
      <div className="next-step-actions">
        <button
          className="next-step-btn primary"
          onClick={() => navigate(def.actionRoute)}
        >
          {def.actionLabel} 
        </button>
      </div>
      <div className="next-step-footer">
        <button
          className="next-step-link"
          onClick={handleReverificar}
          disabled={retrying}
        >
          {retrying ? ' Reverificando...' : ' Reverificar'}
        </button>
        {temReset ? (
          <button
            className="next-step-link"
            onClick={handleLimparReset}
            disabled={retrying}
          >
             Ver tudo de novo
          </button>
        ) : (
          <button
            className="next-step-link danger"
            onClick={handleReiniciar}
            disabled={retrying}
          >
             Reiniciar fluxo
          </button>
        )}
      </div>
    </div>
  );
};

export default NextStepCard;
